import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { loadCurrentUser, logout, updateProfile } from "../../store/authSlice";
import {
  COUNTRY_CALLING_CODES,
  getCountryByIso,
  isoToFlagEmoji,
} from "../data/countryCallingCodes";
import { NAV } from "../Navbar/navbarTheme";
import { fetchQuotes } from "../utils/shopApi";
import AuthFormLayout from "./AuthFormLayout";
import {
  AUTH_PRIMARY,
  authAlertSx,
  authFieldSx,
  authFooterActionsSx,
  authFooterSecondaryButtonSx,
  authLogoutButtonSx,
  authSecondaryButtonSx,
  authPageSx,
  authSubmitButtonSx,
} from "./authStyles";

const LIMITS = {
  name: 60,
  company: 60,
  county: 60,
  phoneLocal: 20,
};

const allowedSimple = /^[a-zA-Z0-9 ]*$/;

const sectionLabelSx = {
  fontSize: "0.75rem",
  fontWeight: 800,
  color: NAV.inkMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 0.5,
};

const findIsoByDial = (dial) => {
  if (!dial) return "GB";
  const match = COUNTRY_CALLING_CODES.find((country) => country.dial === dial);
  return match?.iso ?? "GB";
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, status } = useSelector((state) => state.auth);

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [county, setCounty] = useState("");
  const [countryIso, setCountryIso] = useState("GB");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasQuoteHistory, setHasQuoteHistory] = useState(false);

  const selectedCountry = useMemo(() => {
    return (
      getCountryByIso(countryIso) ??
      getCountryByIso("GB") ?? {
        iso: "GB",
        name: "United Kingdom",
        dial: "+44",
      }
    );
  }, [countryIso]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (token && !user && status !== "loading") {
      dispatch(loadCurrentUser());
    }
  }, [dispatch, token, user, status]);

  useEffect(() => {
    if (!token) {
      navigate("/SignIn", { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!user) return;
    setName(user.name ?? "");
    setCompany(user.company ?? "");
    setCounty(user.county ?? "");
    setCountryIso(findIsoByDial(user.country_code));
    setPhone(String(user.phone ?? "").replace(/\D/g, ""));
  }, [user]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const loadQuoteHistoryAvailability = async () => {
      try {
        const payload = await fetchQuotes({ page: 1 });
        if (cancelled) return;
        const total = payload?.meta?.total ?? payload?.data?.length ?? 0;
        setHasQuoteHistory(total > 0);
      } catch {
        if (!cancelled) {
          setHasQuoteHistory(false);
        }
      }
    };

    loadQuoteHistoryAvailability();

    return () => {
      cancelled = true;
    };
  }, [token, location.pathname]);

  const handleNameChange = (event) => {
    const value = event.target.value;
    if (value.length <= LIMITS.name && allowedSimple.test(value)) {
      setName(value);
    }
  };

  const handleCompanyChange = (event) => {
    const value = event.target.value;
    if (value.length <= LIMITS.company && allowedSimple.test(value)) {
      setCompany(value);
    }
  };

  const handleCountyChange = (event) => {
    const value = event.target.value;
    if (value.length <= LIMITS.county && allowedSimple.test(value)) {
      setCounty(value);
    }
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    if (value.length <= LIMITS.phoneLocal) {
      setPhone(value);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/SignIn", { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    const trimmedName = name.trim();
    const trimmedCompany = company.trim();
    const trimmedCounty = county.trim();

    if (!trimmedName || !trimmedCompany || !trimmedCounty || !phone) {
      setError("Please complete all required fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(
        updateProfile({
          name: trimmedName,
          country_code: selectedCountry.dial,
          phone,
          company: trimmedCompany,
          county: trimmedCounty,
        }),
      ).unwrap();
      setSuccessMessage("Your profile has been updated.");
    } catch (submitError) {
      setError(
        submitError ||
          "Unable to update your profile right now. Please try again later.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return null;
  }

  if (status === "loading" || (status !== "failed" && !user)) {
    return (
      <Box
        component="main"
        sx={{
          ...authPageSx,
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: AUTH_PRIMARY }} />
      </Box>
    );
  }

  if (status === "failed" || !user) {
    return (
      <AuthFormLayout
        title="Profile"
        subtitle="Sign in to view your trade account."
        icon={PersonOutlineIcon}
        footer={
          <Button
            component={Link}
            to="/SignIn"
            variant="contained"
            fullWidth
            sx={authSubmitButtonSx}
          >
            Sign in
          </Button>
        }
      >
        <Alert severity="error" sx={authAlertSx}>
          Unable to load your profile. Please sign in again.
        </Alert>
      </AuthFormLayout>
    );
  }

  const initials = String(user.name || user.email || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <AuthFormLayout
      wide
      title={user.name || "My profile"}
      subtitle={user.email}
      icon={PersonOutlineIcon}
      footer={
        <Stack spacing={1.5} sx={authFooterActionsSx}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              component={Link}
              to="/"
              variant="outlined"
              fullWidth
              sx={authFooterSecondaryButtonSx}
            >
              Back to home
            </Button>
            <Button
              type="button"
              variant="outlined"
              fullWidth
              onClick={handleLogout}
              sx={authLogoutButtonSx}
            >
              Log out
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2.5,
          p: 1.5,
          borderRadius: 2,
          bgcolor: NAV.chipRowBg,
          border: `1px solid ${NAV.line}`,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: "1rem",
            color: "#fff",
            background: `linear-gradient(135deg, ${AUTH_PRIMARY} 0%, #163a5e 100%)`,
            flexShrink: 0,
          }}
        >
          {initials || "?"}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: NAV.ink, lineHeight: 1.3 }}>
            Trade account
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              color: NAV.inkMuted,
              mt: 0.25,
            }}
          >
            {user.company || "Update your company details below."}
          </Typography>
        </Box>
      </Box>

      {hasQuoteHistory ? (
        <Button
          component={Link}
          to="/QuoteHistory"
          variant="outlined"
          fullWidth
          startIcon={<HistoryOutlinedIcon />}
          sx={{ ...authSecondaryButtonSx, mb: 2.5 }}
        >
          Quote history
        </Button>
      ) : null}

      {successMessage ? (
        <Alert severity="success" sx={authAlertSx}>
          {successMessage}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={authAlertSx}>
          {error}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={sectionLabelSx}>Personal details</Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                required
                label="Full name"
                value={name}
                onChange={handleNameChange}
                sx={authFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ color: NAV.inkMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Email address"
                value={user.email ?? ""}
                disabled
                sx={authFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: NAV.inkMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Box>

          <Divider sx={{ borderColor: NAV.line }} />

          <Box>
            <Typography sx={sectionLabelSx}>Business & contact</Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                required
                label="Company name"
                value={company}
                onChange={handleCompanyChange}
                sx={authFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessOutlinedIcon sx={{ color: NAV.inkMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                required
                label="County"
                value={county}
                onChange={handleCountyChange}
                sx={authFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnOutlinedIcon sx={{ color: NAV.inkMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <FormControl fullWidth size="small" sx={authFieldSx}>
                  <InputLabel id="profile-country-label">Country code</InputLabel>
                  <Select
                    labelId="profile-country-label"
                    value={countryIso}
                    label="Country code"
                    onChange={(event) => setCountryIso(event.target.value)}
                  >
                    {COUNTRY_CALLING_CODES.map((country) => (
                      <MenuItem key={country.iso} value={country.iso}>
                        {isoToFlagEmoji(country.iso)} {country.name} ({country.dial})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  required
                  label="Phone number"
                  value={phone}
                  onChange={handlePhoneChange}
                  sx={authFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlinedIcon sx={{ color: NAV.inkMuted, fontSize: 20 }} />
                        <Typography
                          sx={{
                            ml: 0.5,
                            fontSize: "0.875rem",
                            color: NAV.inkMuted,
                            fontWeight: 600,
                          }}
                        >
                          {selectedCountry.dial}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Stack>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isSubmitting}
            sx={authSubmitButtonSx}
          >
            {isSubmitting ? "Saving..." : "Save changes"}
          </Button>
        </Stack>
      </Box>
    </AuthFormLayout>
  );
};

export default ProfilePage;
